import React, { useState, useEffect } from 'react';
import Button from '@salesforce/design-system-react/module/components/button';
import Dropdown from '@salesforce/design-system-react/module/components/menu-dropdown';
import DataTable from '@salesforce/design-system-react/module/components/data-table';
import DataTableColumn from '@salesforce/design-system-react/module/components/data-table/column';
import DataTableCell from '@salesforce/design-system-react/module/components/data-table/cell';
import { PageHeaderControl } from '@salesforce/design-system-react';
import Icon from '@salesforce/design-system-react/module/components/icon';
import Input from '@salesforce/design-system-react/components/input';
import InputIcon from '@salesforce/design-system-react/components/icon/input-icon';
import IconSettings from '@salesforce/design-system-react/components/icon-settings';
import PageHeader from '@salesforce/design-system-react/module/components/page-header';
import debounce from 'lodash.debounce';
import Spinner from '../../components/Spinner';
import { getDateTimeFromTimestamp, getTimeIntervalText } from '../../utilities/datetime';
import { useCallback } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import {
  getFutureBalances,
  FutureBalancesSelector,
} from '../../store/exchanges/futureBalanceSlice';
import { isEmpty } from '@firebase/util';
import { Link, useNavigate } from 'react-router-dom';
import { exchangePageOptions } from '../../constants/defiAccount';
import { getNumbersWithDecimalPlaces, getNumberWithCommas } from '../../utilities/money';
import ProviderLogo from '../../components/shared/ProviderLogo';
import Tooltip from '@salesforce/design-system-react/components/tooltip';

const CustomDataTableName = ({ children, ...props }) => {
  const { item, handleExpand } = props;

  return (
    <DataTableCell
      title={children}
      className="slds-m-right_xx-small slds-m-vertical_large"
      role="gridcell"
    >
      <div
        onClick={(i) =>
          item?.isChildren || !(parseFloat(item?.total_amount) > 0) ? null : handleExpand(i, item)
        }
        style={{
          display: 'flex',
          cursor: `${
            item?.isChildren || !(parseFloat(item?.total_amount) > 0) ? 'default' : 'pointer'
          }`,
        }}
      >
        <div
          className="slds-m-right_small"
          style={{
            width: '98%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {item.isChildren ? (
            item.logo && <img src={item.logo} alt={children} height="30" width="30" />
          ) : (
            <ProviderLogo provider={item?.account_provider} color={item?.account_color} />
          )}{' '}
          {item.isChildren ? children : <Link to={`#`}> {children}</Link>}
        </div>
        {parseFloat(item?.total_amount) > 0 && !item?.isChildren && (
          <div
            style={{
              width: '2%',
              marginRight: '1rem',
            }}
          >
            <Icon
              assistiveText={{ label: 'Account' }}
              category="utility"
              name="chevrondown"
              size="xx-small"
              className="slds-m-left_small"
              style={{
                cursor: 'pointer',
              }}
            />
          </div>
        )}
      </div>
    </DataTableCell>
  );
};
CustomDataTableName.displayName = DataTableCell.displayName;

/**
 * Custom Balance cell to display formatted date
 */
const CustomDataTableBalanceCell = ({ children, ...props }) => {
  const { item } = props;
  const { total_amount } = item;
  const amount = getNumberWithCommas(total_amount || 0, 4);
  return (
    <DataTableCell {...props}>
      <span style={{ fontWeight: '400' }}>${amount}</span>
    </DataTableCell>
  );
};

CustomDataTableBalanceCell.displayName = DataTableCell.displayName;

const CustomLastTransaction = ({ children, ...props }) => {
  return (
    <DataTableCell title={children} {...props}>
      <span className="slds-m-right_xx-small">
        {/* <img src="/assets/icons/menus/transactions.svg" alt=""></img> */}
      </span>
      {children}
    </DataTableCell>
  );
};
CustomLastTransaction.displayName = DataTableCell.displayName;

const CustomDataTableCell = ({ children, ...props }) => {
  return (
    <DataTableCell title={`${children}`} {...props}>
      <p>{children ? getDateTimeFromTimestamp(Number(children)) : '--'}</p>
    </DataTableCell>
  );
};
CustomDataTableCell.displayName = DataTableCell.displayName;

export default function FutureBalances() {
  const dispatch = useDispatch();
  const { futureBalances, mappedAssets, loading, pagination, errors, success, message } =
    useSelector(FutureBalancesSelector);
  const [sortColumn, setSortColumn] = useState('opportunityName');
  const [sortColumnDirection, setSortColumnDirection] = useState({
    opportunityName: 'asc',
  });
  const [items, setItems] = useState([]);
  const [childItems, setChildItems] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState(undefined);
  const [paginated, setPaginated] = useState(0);
  const navigate = useNavigate();
  const [APICalledTime, setAPICalledTime] = useState(new Date());
  const [text, setText] = useState('Recently updated');
  const [searchText, setSearchText] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [rows, setRowData] = React.useState('');
  const [query, setQuery] = React.useState('');

  const handleExpand = (event, row) => {
    //cloning the items to prevent mutation
    //...
    const _items = JSON.parse(JSON.stringify(items));

    /**
     * Setting the portfolio details for each wallet in the same index
     * as the wallet in the items array.
     */
    let childRows = JSON.parse(JSON.stringify(childItems));

    if (expandedRows?.id === row._id) {
      /**
       * If any one row is currently enabled
       * And same row is clicked, remove its child items
       */

      // Finding the index of the clicked row in the items array
      //...
      let index = _items.map((item) => item._id).indexOf(row._id);

      // Removing the child items from index + 1 to the number of children appended
      //...
      _items.splice(index + 1, childRows.length);

      _items[index].classNameRow = 'expandables';

      // Setting new Element in the items state
      //...
      setItems(_items);

      // Since it was the same element clicked, current expanded row is undefined
      setExpandedRows(undefined);
    } else {
      /**
       *
       * If the clicked row is entirely new
       * Check if there was existing expanded row
       *
       * Find the index of the current clicked row in the items array
       * Append the children row next to its index to make it appear like child in table
       *
       */

      if (expandedRows?.id) {
        /**
         * If there is expanded element in the table,
         * Find the index of the expanded row
         */
        let previousIndex = _items.map((item) => item._id).indexOf(expandedRows.id);

        /**
         * Remove the child element which was appended just below the parent index
         */
        _items.splice(previousIndex + 1, childRows.length);
      }

      childRows = [];

      // setting childRows value
      const currentWallet = [...mappedAssets[row._id]];
      currentWallet.forEach((data, index) => {
        if (data?.total && Math.round(parseFloat(data?.total) * 100000) === 0) {
          return;
        }

        childRows.push({
          _id: row._id + '-' + index,
          account_name: data.asset,
          total_amount: `${getNumberWithCommas(getNumbersWithDecimalPlaces(data.total, 4), 6)}`,
          logo: data.logo,
          isChildren: true,
        });
      });

      setChildItems(childRows);

      let index = _items.map((item) => item._id).indexOf(row._id);

      let newItems = [
        ..._items.slice(0, index + 1),
        ...childRows.map((c, index) => {
          c.classNameRow = index % 2 === 0 ? `expanded-children even` : `expanded-children odd`;
          return c;
        }),
        ..._items.slice(index + 1),
      ];
      newItems[index].classNameRow = 'expanded';
      setExpandedRows({ id: row._id });
      setItems(newItems);
    }
  };

  useEffect(() => {
    dispatch(getFutureBalances());
  }, [dispatch]);

  useEffect(() => {
    setHasMore(true);
    if (futureBalances.length > 0) {
      setItems(futureBalances);
    }
    setAPICalledTime(new Date());
    const data = getTimeIntervalText(APICalledTime);
    setText(data);
  }, [futureBalances]);

  useEffect(() => {
    const interval = setInterval(() => {
      const data = getTimeIntervalText(APICalledTime);
      setText(data);
    }, 20000);

    return () => clearInterval(interval);
  }, [APICalledTime]);

  const handleNetworkSort = (sortColumn, ...rest) => {
    const { property, sortDirection } = sortColumn;
    setSortColumn(property);
    setSortColumnDirection(sortDirection);
    setItems([]);
    setHasMore(true);
    setIsPageLoading(true);
    setPaginated(0);
    dispatch(getFutureBalances({ sort_column: property, sort_order: sortDirection }));

    // sorting will actually be handled from api
    // so dispatch sort action in useEffect
    // dispatch(sortAccounte({sortDirection, sortProperty}))
    // and the update the state with sorted accounts lists this will be done by above useEffect
  };

  const handleLocalSort = (sortColumn, ...rest) => {
    const sortProperty = sortColumn.property;

    const { sortDirection } = sortColumn;

    setSortColumn(sortProperty);
    setSortColumnDirection({
      [sortProperty]: sortDirection,
    });

    // sorting will actually be handled from api
    // so dispatch sort action in useEffect
    // dispatch(sortAccounte({sortDirection, sortProperty}))
    // and the update the state with sorted accounts lists this will be done by above useEffect

    /**
     * For now handling from FED
     * later can be changed
     */
    let clone = [...items];

    clone.sort((a, b) => {
      if (!a[sortProperty]) {
        return 0;
      }
      if (typeof a[sortProperty] === 'string' && typeof b[sortProperty] === 'string') {
        return sortDirection === 'asc'
          ? a[sortProperty].localeCompare(b[sortProperty])
          : b[sortProperty].localeCompare(a[sortProperty]);
      }
      if (typeof a[sortProperty] === 'number' && typeof b[sortProperty] === 'number') {
        return sortDirection === 'asc'
          ? a[sortProperty] - b[sortProperty]
          : b[sortProperty] - a[sortProperty];
      }

      return 0;
    });

    if (JSON.stringify(clone) === JSON.stringify(items)) {
      return;
    }

    setItems(clone);
  };

  const handleSort = (sortColumn, ...rest) => {
    if (!pagination.hasNextPage) {
      handleLocalSort(sortColumn, ...rest);
    } else {
      handleNetworkSort(sortColumn, ...rest);
    }
  };



  const handleLoadMore = () => {
    if (!loading && !isPageLoading) {
      if (pagination?.hasNextPage) {
        setIsPageLoading(true);
        setPaginated(paginated + 1);
        setTimeout(() => {
          if (pagination.pages <= 1 || paginated >= pagination.pages || !pagination.hasNextPage) {
            setHasMore(false);
            setIsPageLoading(false);
            return;
          }

          if (pagination.currentPage >= pagination.pages) {
            setHasMore(false);
            setIsPageLoading(false);
            return;
          }
          let url = new URLSearchParams(pagination.nextPageUrl);

          dispatch(
            getFutureBalances({
              limit: url.get('limit'),
              offset: url.get('offset'),
              q: searchText,
              sort_column: sortColumn,
              sort_order: sortColumnDirection,
            })
          );
          setIsPageLoading(false);
        }, 1000);
      } else {
        setIsPageLoading(false);
        setHasMore(false);
      }
    }
  };

  const refreshTable = () => {
    setHasMore(true);
    setPaginated(0);
    setItems([]);
    if (searchText !== '') {
      searchHandler(searchText);
    } else {
      dispatch(getFutureBalances());
    }
  };

  // const searchHandler = (value) => {
  //   setHasMore(false);
  //   // setItems([]);
  //   // setPaginated(0);
  //   setQuery(value);
  //   const results = items.filter((row) => {
  //     const regex = new RegExp(value, "gi");
  //     return row.name.match(regex);
  //   });
  //   setRowData(results);

  // };
  // const items = rows.length === 0 && !query ? items : rows;

  const searchHandler = useCallback(
    debounce((values) => {
      setHasMore(true);
      setItems([]);
      setPaginated(0);
      dispatch(
        getFutureBalances({
          q: values,
        })
      );
    }, 1000),
    []
  );

  const onSearchText = (value) => {
    setSearchText(value);
    searchHandler(value);
  };

  const controls = () => (
    <React.Fragment>
      <PageHeaderControl>
        <IconSettings iconPath="/assets/icons">
          <div className="">
            <Input
              iconLeft={
                <InputIcon
                  assistiveText={{
                    icon: 'Search',
                  }}
                  name="search"
                  category="utility"
                />
              }
              id="search_text"
              placeholder="Search Future Balance"
              value={searchText}
              onChange={(e) => onSearchText(e.target.value)}
              fixedTextRight={
                <Tooltip
                  id="search-tooltip"
                  align="top right"
                  content="Search works on the basis of name"
                  position='overflowBoundaryElement'
                />
              }
            />
          </div>
        </IconSettings>
      </PageHeaderControl>
      <PageHeaderControl>
        <Button
          assistiveText={{ icon: 'Refresh' }}
          iconCategory="utility"
          iconName="refresh"
          iconVariant="border-filled"
          variant="icon"
          onClick={() => {
            refreshTable();
          }}
        />
      </PageHeaderControl>
    </React.Fragment>
  );

  if (success && showToast) {
    toast.success(message, {
      id: 'exchange-transaction-success',
    });
    setShowToast(false);
  }

  if (!isEmpty(errors) && showToast) {
    toast.error(errors, {
      id: 'exchange-transaction-error',
    });
    setShowToast(false);
  }

  return (
    <>
      {isPageLoading ? (
        <Spinner />
      ) : (
        <div className="pageContent">
          <Toaster position="top-right" reverseOrder={false} />
          <PageHeader
            icon={<Icon assistiveText={{ label: 'Accounts' }} category="standard" name="account" />}
            info={`${items && items.length} items • ${text}`}
            joined
            label="Exchanges Future Balance"
            title="All Future Balances"
            nameSwitcherDropdown={
              <Dropdown
                assistiveText={{ icon: 'Name Switcher' }}
                buttonClassName="slds-button_icon-small"
                buttonVariant="icon"
                iconCategory="utility"
                iconName="down"
                onSelect={(e) => {
                  navigate('/exchanges/' + e.value);
                }}
                id="page-header-name-switcher-dropdown"
                options={exchangePageOptions}
              />
            }
            // onRenderActions={() =>
            //   Actions({
            //     onAddAction: hanldeAccountAdd,
            //     onDropDownSelect: (value) => {
            //       setAccountsDropDown(value);
            //     },
            //     value: accountsDropDown,
            //     options: [
            //       { label: 'Choose', value: '0' },
            //       { label: 'Menu Item One', value: 'A0' },
            //       { label: 'Menu Item Two', value: 'B0' },
            //       { label: 'Menu Item Three', value: 'C0' },
            //       { type: 'divider' },
            //       { label: 'Menu Item Four', value: 'D0' },
            //     ],
            //   })
            // }
            onRenderControls={controls}
            truncate
            variant="object-home"
          />

          <div
            style={{ height: `${futureBalances.length === 0 ? '100%' : 'calc(100vh - 220px)'}` }}
          >
            <DataTable
              assistiveText={{
                actionsHeader: 'actions',
                columnSort: 'sort this column',
                columnSortedAscending: 'asc',
                columnSortedDescending: 'desc',
                selectAllRows: 'Select all rows',
                selectRow: 'Select this row',
              }}
              fixedHeader
              fixedLayout
              items={items}
              id="DataTableExample-FixedHeaders"
              onSort={handleSort}
              hasMore={hasMore}
              onLoadMore={handleLoadMore}
              noRowHover
            >
              <DataTableColumn
                isSorted={true}
                label="Name"
                primaryColumn
                property="account_name"
                sortable
                width="20rem"
              >
                <CustomDataTableName className="test--class--for" handleExpand={handleExpand} />
              </DataTableColumn>
              <DataTableColumn label="Balance" sortable property="total_amount">
                <CustomDataTableBalanceCell />
              </DataTableColumn>
              <DataTableColumn sortable isDefaultSortDescending label="Label" property="label" />
              <DataTableColumn label="Tags" property="tags" sortable />
              <DataTableColumn label="Last Transaction" property="last_transaction" sortable>
                <CustomLastTransaction />
              </DataTableColumn>
              <DataTableColumn
                label="Last Transaction Date"
                property="update_time"
                width={items.length === 0 ? '10rem' : '12rem'}
                sortable
                sortDirection={sortColumnDirection.update_time}
              >
                <CustomDataTableCell />
              </DataTableColumn>
            </DataTable>
            {(futureBalances.length === 0 || items.length === 0) && !hasMore && (
              <div className="nodatafound">
                <div>
                  <img
                    src="/assets/empty-state/empty-folder.png"
                    alt=""
                    style={{ height: '150px' }}
                  />
                  <p style={{ textAlign: 'center', fontWeight: '700', fontSize: '20px' }}>
                    Data not available
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
